(() => {
  const normalize = (value) => (value || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const matchesWord = (words, token) => {
    if (token.length <= 3) return words.includes(token);
    return words.some((word) => word.includes(token));
  };

  const matchesNegativePhrase = (haystack, subject) => {
    const phrases = [
      `no ${subject}`,
      `without ${subject}`,
      `${subject} absent`,
      `${subject} unavailable`,
      `${subject} not available`
    ];

    return phrases.some((phrase) => haystack.includes(phrase));
  };

  const matchesSearch = (item, tokens) => {
    const haystack = normalize(item.dataset.search || '');
    const words = haystack ? haystack.split(' ') : [];
    const remainingTokens = [...tokens];
    const noIndex = remainingTokens.indexOf('no');

    if (noIndex !== -1 && remainingTokens[noIndex + 1]) {
      const subject = remainingTokens[noIndex + 1];
      if (!matchesNegativePhrase(haystack, subject)) return false;
      remainingTokens.splice(noIndex, 2);
    }

    return remainingTokens.every((token) => {
      if (token === 'no') {
        return words.some((word) => ['no', 'not', 'without', 'absent', 'missing'].includes(word));
      }
      return matchesWord(words, token);
    });
  };

  const refreshPanelCount = (panel) => {
    const count = panel.querySelectorAll('[data-troubleshooting-issue]').length;
    const note = panel.querySelector('.gs-troubleshooting__verified-note span');
    if (note) note.textContent = window.gsPlural('troubleshooting.records', count);
  };

  const resolvePaginationUrl = (rawUrl) => {
    if (!rawUrl) return '';

    try {
      const paginationUrl = new URL(rawUrl, window.location.href);
      const currentUrl = new URL(window.location.href);

      // Shopify theme previews add query parameters that are not included in
      // paginate.next.url. Keep the preview context and merge only the
      // pagination URL's path/query values into it.
      if (paginationUrl.pathname) currentUrl.pathname = paginationUrl.pathname;
      paginationUrl.searchParams.forEach((value, key) => {
        currentUrl.searchParams.set(key, value);
      });
      currentUrl.hash = '';

      return currentUrl.toString();
    } catch (error) {
      return rawUrl;
    }
  };

  const hydrateAdditionalIssuePages = async (root) => {
    const host = root.querySelector('[data-troubleshooting-page-fragment]');
    if (!host) return;

    let nextUrl = resolvePaginationUrl(host.dataset.troubleshootingNextUrl || '');
    const seen = new Set();
    root.dataset.troubleshootingLoadState = nextUrl ? 'loading' : 'complete';

    while (nextUrl && !seen.has(nextUrl)) {
      seen.add(nextUrl);

      try {
        const response = await fetch(nextUrl, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) {
          root.dataset.troubleshootingLoadState = 'partial';
          break;
        }

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incomingFragment = parsed.querySelector('[data-troubleshooting-page-fragment]');
        if (!incomingFragment) {
          root.dataset.troubleshootingLoadState = 'partial';
          break;
        }

        Array.from(incomingFragment.querySelectorAll('[data-troubleshooting-brand-panel]')).forEach((incomingPanel) => {
          const brandHandle = incomingPanel.dataset.troubleshootingBrandPanel || '';
          const existingPanel = Array.from(host.querySelectorAll('[data-troubleshooting-brand-panel]'))
            .find((panel) => (panel.dataset.troubleshootingBrandPanel || '') === brandHandle);

          if (existingPanel) {
            const target = existingPanel.querySelector('.gs-troubleshooting__issues');
            if (target) {
              Array.from(incomingPanel.querySelectorAll('[data-troubleshooting-issue]')).forEach((issue) => {
                target.appendChild(document.importNode(issue, true));
              });
              refreshPanelCount(existingPanel);
            }
          } else {
            const imported = document.importNode(incomingPanel, true);
            host.appendChild(imported);
            refreshPanelCount(imported);
          }
        });

        nextUrl = resolvePaginationUrl(incomingFragment.dataset.troubleshootingNextUrl || '');
      } catch (error) {
        root.dataset.troubleshootingLoadState = 'partial';
        break;
      }
    }

    host.dataset.troubleshootingNextUrl = '';
    if (root.dataset.troubleshootingLoadState !== 'partial') {
      root.dataset.troubleshootingLoadState = 'complete';
    }
  };

  const initializeRoot = async (root) => {
    const queuedActivations = [];
    const earlyInteractive = Array.from(root.querySelectorAll(
      '[data-troubleshooting-filter-kind], .gs-troubleshooting__brand'
    ));

    const queueEarlyActivation = (event) => {
      if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
      if (root.dataset.troubleshootingReady === 'true') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const target = event.currentTarget;
      queuedActivations.push(target);
      target.classList.add('is-pending-activation');
      root.setAttribute('aria-busy', 'true');
    };

    earlyInteractive.forEach((item) => {
      item.addEventListener('click', queueEarlyActivation);
      item.addEventListener('keydown', queueEarlyActivation);
    });

    await hydrateAdditionalIssuePages(root);

    earlyInteractive.forEach((item) => {
      item.removeEventListener('click', queueEarlyActivation);
      item.removeEventListener('keydown', queueEarlyActivation);
    });

    const input = root.querySelector('[data-troubleshooting-search]');
    const clear = root.querySelector('[data-troubleshooting-clear]');
    const emptyClear = root.querySelector('[data-troubleshooting-empty-clear]');
    const status = root.querySelector('[data-troubleshooting-status]');
    const empty = root.querySelector('[data-troubleshooting-empty]');
    const items = Array.from(root.querySelectorAll('[data-search-item]'));
    const groups = Array.from(root.querySelectorAll('[data-search-group]'));
    const panels = Array.from(root.querySelectorAll('[data-troubleshooting-brand-panel]'));
    const brandCards = Array.from(root.querySelectorAll('.gs-troubleshooting__brand'));
    const filterCards = Array.from(root.querySelectorAll('[data-troubleshooting-filter-kind]'));
    const discoveryGroups = Array.from(new Set(filterCards.map((card) => card.closest('[data-search-group]')).filter(Boolean)));
    const baseItems = items.filter((item) => !item.closest('[data-troubleshooting-brand-panel]'));
    const baseGroups = groups.filter((group) => !group.matches('[data-troubleshooting-brand-panel]'));
    const modelState = new Map();
    const modelControls = new Map();
    let selectedBrand = '';
    let selectedFilter = null;

    let scrollCorrectionTimer = null;
    let scrollSequence = 0;

    const cancelPendingScroll = () => {
      scrollSequence += 1;
      if (scrollCorrectionTimer) {
        window.clearTimeout(scrollCorrectionTimer);
        scrollCorrectionTimer = null;
      }
    };

    const getStickyOffset = () => {
      const candidates = [
        document.querySelector('.section-header.shopify-section-group-header-group'),
        document.querySelector('sticky-header'),
        document.querySelector('.header-wrapper')
      ].filter(Boolean);

      let bottom = 0;
      candidates.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom > 0 && rect.top <= 8) bottom = Math.max(bottom, rect.bottom);
      });

      return Math.max(20, Math.round(bottom + 18));
    };

    const scrollToTarget = (target) => {
      if (!target) return;

      cancelPendingScroll();
      const sequence = scrollSequence;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const perform = (behavior) => {
        if (sequence !== scrollSequence) return;
        const top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - getStickyOffset());
        window.scrollTo({ top, behavior });
      };

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          perform(reduceMotion ? 'auto' : 'smooth');

          if (!reduceMotion) {
            scrollCorrectionTimer = window.setTimeout(() => {
              if (sequence !== scrollSequence) return;
              const delta = target.getBoundingClientRect().top - getStickyOffset();
              if (Math.abs(delta) > 6) perform('auto');
              scrollCorrectionTimer = null;
            }, 420);
          }
        });
      });
    };

    if (!input || !items.length) return;

    // Model family comes from the stable data attribute rendered by gs-troubleshooting-verified
    // (raw metaobject model_scope), never from the translated "Verified for ..." label.
    const getModelScope = (item) => (item.dataset.gsModelFamily || '').trim();

    const areaMatchesCategory = (rawArea, category) => {
      const area = normalize(rawArea);
      const groups = {
        power: [
          'power', 'battery', 'charging', 'charger', 'temperature', 'overheat', 'protection',
          'strom', 'batterie', 'laden', 'aufladen', 'lade', 'temperatur', 'schutz',
          'alimentation', 'batterie', 'charge', 'recharge', 'temperature', 'protection'
        ],
        water: [
          'water', 'mop', 'mopping', 'drain', 'tank', 'detergent', 'self cleaning', 'drying', 'spray',
          'wasser', 'wischen', 'mopp', 'tank', 'ablauf', 'reinigung', 'trocknung',
          'eau', 'lavage', 'serpilliere', 'reservoir', 'vidange', 'detergent', 'sechage', 'auto nettoyage'
        ],
        suction: [
          'suction', 'airflow', 'vacuum motor', 'dust collection', 'dust bin', 'dustbin', 'filter', 'pickup',
          'saugen', 'saugleistung', 'luftstrom', 'staub', 'filter',
          'aspiration', 'flux d air', 'poussiere', 'filtre', 'collecte'
        ],
        brushes: [
          'brush', 'brushes', 'roller', 'rollers', 'extractor', 'scrubber',
          'burste', 'bursten', 'walze', 'walzen',
          'brosse', 'brosses', 'rouleau', 'rouleaux', 'extracteur'
        ],
        navigation: [
          'navigation', 'sensor', 'sensors', 'lidar', 'mapping', 'vision', 'bumper', 'cliff', 'obstacle', 'mobility', 'drive wheel', 'terrain', 'position', 'perception', 'edge detection', 'traction',
          'sensoren', 'kartierung', 'stossfanger', 'hindernis', 'mobilitat', 'antriebsrad',
          'capteur', 'capteurs', 'cartographie', 'pare chocs', 'mobilite', 'roue motrice', 'detection de bord'
        ],
        docks: [
          'dock', 'docking', 'base station', 'station communication', 'home base', 'auto empty', 'auto emptying', 'automatic refill',
          'basisstation', 'station', 'entleerung', 'automatische entleerung', 'automatische nachfullung',
          'station', 'base', 'vidage', 'auto vidage', 'remplissage automatique'
        ]
      };
      return (groups[category] || []).some((term) => area.includes(normalize(term)));
    };

    const applianceMatches = (rawAppliance, target) => {
      const raw = normalize(rawAppliance);
      const wanted = normalize(target);

      const aliases = {
        robot: [
          'robot vacuum', 'robot vacuums', 'robot vacuum docks',
          'saugroboter', 'saugroboter stationen', 'stationen',
          'aspirateurs robots', 'aspirateurs robots et stations', 'aspirateur robot', 'stations'
        ],
        cordless: [
          'cordless', 'stick vacuum', 'handheld',
          'akku stielstaubsauger', 'kabellos', 'stielstaubsauger', 'handstaubsauger',
          'aspirateurs balais sans fil', 'sans fil', 'aspirateur balai', 'aspirateurs balais'
        ],
        wetdry: [
          'wet dry', 'floor washer', 'floor cleaner',
          'nass trockensauger', 'nass trocken', 'bodenreiniger', 'wischsauger',
          'aspirateurs laveurs', 'aspirateur laveur', 'laveur de sols', 'laveurs de sols', 'sec et humide'
        ],
        window: [
          'window cleaning', 'window cleaner',
          'fensterreinigungsroboter', 'fensterputzroboter',
          'robots lave vitres', 'robot lave vitre'
        ],
        pool: [
          'pool cleaning', 'pool cleaner',
          'poolreinigungsroboter', 'poolroboter',
          'robots de piscine', 'robot de piscine', 'robot piscine', 'robots nettoyeurs de piscine'
        ],
        mower: [
          'robotic lawn mower', 'lawn mower', 'robot mower',
          'mahroboter', 'maehroboter', 'rasenmahroboter',
          'robots tondeuses', 'robot tondeuse'
        ]
      };

      return (aliases[wanted] || []).some((term) => raw.includes(normalize(term)));
    };

    const issueMatchesFilter = (item, filter) => {
      if (!filter) return true;

      const stableTerms = normalize(item.dataset.gsFilterTerms || '');

      if (filter.kind === 'appliance') {
        return stableTerms.includes(normalize('gsappliance_' + filter.value));
      }

      if (filter.kind === 'area') {
        return stableTerms.includes(normalize('gsarea_' + filter.value));
      }

      return true;
    };

    const brandGroup = brandCards[0]?.closest('[data-search-group]') || null;

    const filterSummary = document.createElement('div');
    filterSummary.className = 'gs-troubleshooting__filter-summary';
    filterSummary.hidden = true;

    const filterSummaryPrefix = document.createElement('span');
    filterSummaryPrefix.className = 'gs-troubleshooting__filter-summary-prefix';
    filterSummaryPrefix.textContent = window.gsT('troubleshooting.filter_selected');

    const filterSummaryValue = document.createElement('strong');
    filterSummaryValue.className = 'gs-troubleshooting__filter-summary-value';

    const filterSummaryClear = document.createElement('button');
    filterSummaryClear.type = 'button';
    filterSummaryClear.className = 'gs-troubleshooting__filter-summary-clear';
    filterSummaryClear.textContent = window.gsT('troubleshooting.clear_filter');

    filterSummary.appendChild(filterSummaryPrefix);
    filterSummary.appendChild(filterSummaryValue);
    filterSummary.appendChild(filterSummaryClear);

    if (brandGroup) brandGroup.before(filterSummary);

    const getSelectedFilterLabel = () => {
      if (!selectedFilter) return '';
      const activeCard = filterCards.find((card) =>
        card.dataset.troubleshootingFilterKind === selectedFilter.kind
        && card.dataset.troubleshootingFilterValue === selectedFilter.value
      );
      return (activeCard?.querySelector('h3')?.textContent || '').trim();
    };

    const updatePanelDisplayedCount = (panel, count) => {
      const note = panel.querySelector('.gs-troubleshooting__verified-note span');
      if (note) note.textContent = window.gsPlural('troubleshooting.records', count);
    };

    const updateModelControl = (panel, control, eligibleIssues) => {
      if (!control) return { hasChoice: false, eligibleScopes: [] };

      const eligibleScopes = [];
      eligibleIssues.forEach((item) => {
        const scope = getModelScope(item);
        if (scope && !eligibleScopes.includes(scope)) eligibleScopes.push(scope);
      });

      control.buttons.forEach((button) => {
        const model = button.dataset.troubleshootingModel || '';
        if (model === '__all__') {
          button.hidden = eligibleIssues.length === 0;
          button.textContent = window.gsT('troubleshooting.all_issues', { count: eligibleIssues.length });
          return;
        }

        const count = eligibleIssues.filter((item) => getModelScope(item) === model).length;
        button.hidden = count === 0;
        button.textContent = `${model} (${count})`;
      });

      const hasChoice = eligibleScopes.length > 1;
      control.wrapper.hidden = !hasChoice;
      return { hasChoice, eligibleScopes };
    };

    const panelByName = new Map();
    panels.forEach((panel) => {
      panelByName.set(normalize(panel.dataset.troubleshootingBrandName), panel);
      panel.hidden = true;
      refreshPanelCount(panel);

      const panelIssues = Array.from(panel.querySelectorAll('[data-troubleshooting-issue]'));
      const scopes = [];
      panelIssues.forEach((item) => {
        const scope = getModelScope(item);
        if (scope && !scopes.includes(scope)) scopes.push(scope);
      });

      if (scopes.length > 1) {
        const wrapper = document.createElement('div');
        wrapper.className = 'gs-troubleshooting__model-filter';
        wrapper.hidden = true;

        const heading = document.createElement('div');
        heading.className = 'gs-troubleshooting__model-filter-head';
        const headingTitle = document.createElement('strong');
        headingTitle.textContent = window.gsT('troubleshooting.choose_model_family');
        const headingText = document.createElement('span');
        headingText.textContent = window.gsT('troubleshooting.choose_model_family_hint');
        heading.appendChild(headingTitle);
        heading.appendChild(headingText);
        wrapper.appendChild(heading);

        const buttonsWrap = document.createElement('div');
        buttonsWrap.className = 'gs-troubleshooting__model-buttons';
        wrapper.appendChild(buttonsWrap);

        const buttons = [];
        scopes.forEach((scope) => {
          const count = panelIssues.filter((item) => getModelScope(item) === scope).length;
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'gs-troubleshooting__model-button';
          button.textContent = `${scope} (${count})`;
          button.dataset.troubleshootingModel = scope;
          button.setAttribute('aria-pressed', 'false');
          buttonsWrap.appendChild(button);
          buttons.push(button);
        });

        const allButton = document.createElement('button');
        allButton.type = 'button';
        allButton.className = 'gs-troubleshooting__model-button gs-troubleshooting__model-button--all';
        allButton.textContent = window.gsT('troubleshooting.all_issues', { count: panelIssues.length });
        allButton.dataset.troubleshootingModel = '__all__';
        allButton.setAttribute('aria-pressed', 'false');
        buttonsWrap.appendChild(allButton);
        buttons.push(allButton);

        const issuesContainer = panel.querySelector('.gs-troubleshooting__issues');
        if (issuesContainer) issuesContainer.before(wrapper);

        const brandHandle = panel.dataset.troubleshootingBrandPanel || '';
        modelControls.set(brandHandle, { wrapper, buttons, scopes });

        buttons.forEach((button) => {
          button.addEventListener('click', () => {
            modelState.set(brandHandle, button.dataset.troubleshootingModel || '');
            run();
            scrollToTarget(issuesContainer || panel);
          });
        });
      }
    });

    brandCards.forEach((card) => {
      const cardName = normalize(card.dataset.troubleshootingBrandName || card.querySelector('h3')?.textContent);
      const panel = panelByName.get(cardName);
      const description = card.querySelector('p');

      if (!panel) {
        card.classList.add('is-pending');
        card.setAttribute('aria-disabled', 'true');
        if (description) description.textContent = window.gsT('troubleshooting.coverage_pending');
        return;
      }

      const brandHandle = panel.dataset.troubleshootingBrandPanel || '';
      card.dataset.troubleshootingBrandCard = brandHandle;
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-pressed', 'false');
      card.setAttribute('aria-controls', panel.id);
      card.classList.add('is-available');
      if (description) description.textContent = window.gsT('troubleshooting.view_verified');

      const activate = () => {
        const hadDirectSearch = Boolean(normalize(input.value));
        const changed = selectedBrand !== brandHandle;

        // A brand chosen from direct search becomes the new navigation path.
        // Do not silently re-apply an older hidden appliance/problem filter.
        if (hadDirectSearch) {
          selectedFilter = null;
          modelState.clear();
        }

        selectedBrand = brandHandle;
        if (changed) modelState.delete(brandHandle);
        input.value = '';
        run();
        scrollToTarget(panel);
      };

      card.addEventListener('click', activate);
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate();
        }
      });
    });

    filterCards.forEach((card) => {
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-pressed', 'false');

      const activate = () => {
        const next = {
          kind: card.dataset.troubleshootingFilterKind || '',
          value: card.dataset.troubleshootingFilterValue || ''
        };
        const changed = !selectedFilter
          || selectedFilter.kind !== next.kind
          || selectedFilter.value !== next.value;

        selectedFilter = next;
        if (changed) {
          selectedBrand = '';
          modelState.clear();
        }
        input.value = '';
        run();
        scrollToTarget(filterSummary || brandGroup);
      };

      card.addEventListener('click', activate);
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate();
        }
      });
    });

    const run = () => {
      const query = normalize(input.value);
      const tokens = query ? query.split(' ') : [];
      let baseVisible = 0;
      let issueVisible = 0;

      if (query) {
        discoveryGroups.forEach((group) => { group.hidden = false; });
        baseItems.forEach((item) => {
          const matches = matchesSearch(item, tokens);
          item.hidden = !matches;
          if (matches) baseVisible += 1;
        });

        baseGroups.forEach((group) => {
          const hasVisible = Array.from(group.querySelectorAll(':scope > * [data-search-item], :scope > [data-search-item]'))
            .filter((item) => !item.closest('[data-troubleshooting-brand-panel]'))
            .some((item) => !item.hidden);
          group.hidden = !hasVisible;
        });

        panels.forEach((panel) => {
          const brandHandle = panel.dataset.troubleshootingBrandPanel || '';
          const control = modelControls.get(brandHandle);
          if (control) control.wrapper.hidden = true;

          const panelIssues = Array.from(panel.querySelectorAll('[data-troubleshooting-issue]'));
          let panelMatches = 0;

          panelIssues.forEach((item) => {
            const matches = matchesSearch(item, tokens);
            item.hidden = !matches;
            if (matches) panelMatches += 1;
          });

          panel.hidden = panelMatches === 0;
          updatePanelDisplayedCount(panel, panelMatches);
          issueVisible += panelMatches;
        });
      } else if (selectedFilter) {
        baseItems.forEach((item) => { item.hidden = false; });
        baseGroups.forEach((group) => { group.hidden = false; });
        discoveryGroups.forEach((group) => { group.hidden = true; });

        const eligibleByBrand = new Map();
        let totalEligible = 0;

        panels.forEach((panel) => {
          const brandHandle = panel.dataset.troubleshootingBrandPanel || '';
          const panelIssues = Array.from(panel.querySelectorAll('[data-troubleshooting-issue]'));
          const eligibleIssues = panelIssues.filter((item) => issueMatchesFilter(item, selectedFilter));
          eligibleByBrand.set(brandHandle, eligibleIssues);
          totalEligible += eligibleIssues.length;

          const isSelected = Boolean(selectedBrand) && brandHandle === selectedBrand;
          panel.hidden = !isSelected || eligibleIssues.length === 0;

          const control = modelControls.get(brandHandle);

          if (!isSelected) {
            panelIssues.forEach((item) => { item.hidden = false; });
            if (control) control.wrapper.hidden = true;
            return;
          }

          const { hasChoice } = updateModelControl(panel, control, eligibleIssues);
          const selectedModel = modelState.get(brandHandle) || '';

          if (control) {
            control.buttons.forEach((button) => {
              const active = button.dataset.troubleshootingModel === selectedModel;
              button.classList.toggle('is-active', active);
              button.setAttribute('aria-pressed', active ? 'true' : 'false');
            });
          }

          let visibleForPanel = 0;
          panelIssues.forEach((item) => {
            const eligible = eligibleIssues.includes(item);
            let matches = eligible;

            if (eligible && hasChoice) {
              matches = selectedModel === '__all__' || (selectedModel && getModelScope(item) === selectedModel);
            }

            item.hidden = !matches;
            if (matches) {
              issueVisible += 1;
              visibleForPanel += 1;
            }
          });

          updatePanelDisplayedCount(panel, hasChoice && !selectedModel ? eligibleIssues.length : visibleForPanel);
        });

        brandCards.forEach((card) => {
          const brandHandle = card.dataset.troubleshootingBrandCard || '';
          const eligibleIssues = eligibleByBrand.get(brandHandle) || [];
          card.hidden = eligibleIssues.length === 0;
        });

        if (!selectedBrand) issueVisible = totalEligible;
      } else {
        baseItems.forEach((item) => { item.hidden = false; });
        baseGroups.forEach((group) => { group.hidden = false; });
        discoveryGroups.forEach((group) => { group.hidden = false; });
        brandCards.forEach((card) => { card.hidden = false; });

        panels.forEach((panel) => {
          const brandHandle = panel.dataset.troubleshootingBrandPanel || '';
          const isSelected = Boolean(selectedBrand) && brandHandle === selectedBrand;
          panel.hidden = !isSelected;

          const panelIssues = Array.from(panel.querySelectorAll('[data-troubleshooting-issue]'));
          const control = modelControls.get(brandHandle);
          updatePanelDisplayedCount(panel, panelIssues.length);
          if (control) updateModelControl(panel, control, panelIssues);

          if (!isSelected) {
            panelIssues.forEach((item) => { item.hidden = false; });
            if (control) control.wrapper.hidden = true;
            return;
          }

          if (!control) {
            panelIssues.forEach((item) => { item.hidden = false; });
            issueVisible += panelIssues.length;
            return;
          }

          control.wrapper.hidden = false;
          const selectedModel = modelState.get(brandHandle) || '';
          control.buttons.forEach((button) => {
            const active = button.dataset.troubleshootingModel === selectedModel;
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-pressed', active ? 'true' : 'false');
          });

          let visibleForPanel = 0;
          panelIssues.forEach((item) => {
            const matches = selectedModel === '__all__' || (selectedModel && getModelScope(item) === selectedModel);
            item.hidden = !matches;
            if (matches) {
              issueVisible += 1;
              visibleForPanel += 1;
            }
          });
          if (selectedModel) updatePanelDisplayedCount(panel, visibleForPanel);
        });
      }

      filterCards.forEach((card) => {
        const active = !query && selectedFilter
          && card.dataset.troubleshootingFilterKind === selectedFilter.kind
          && card.dataset.troubleshootingFilterValue === selectedFilter.value;
        card.classList.toggle('is-active', Boolean(active));
        card.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      brandCards.forEach((card) => {
        const active = !query && selectedBrand && card.dataset.troubleshootingBrandCard === selectedBrand;
        card.classList.toggle('is-active', Boolean(active));
        if (card.hasAttribute('aria-pressed')) card.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (filterSummary) {
        const showSummary = !query && Boolean(selectedFilter);
        filterSummary.hidden = !showSummary;
        if (showSummary) filterSummaryValue.textContent = getSelectedFilterLabel();
      }

      if (clear) clear.hidden = !query;
      if (empty) empty.hidden = !query || (baseVisible + issueVisible) > 0;

      if (status) {
        if (query) {
          if (issueVisible > 0) {
            status.textContent = window.gsPlural('troubleshooting.results', issueVisible);
          } else {
            status.textContent = window.gsPlural('troubleshooting.areas', baseVisible);
          }
        } else if (selectedFilter) {
          if (!selectedBrand) {
            status.textContent = window.gsPlural('troubleshooting.filter_choose_brand', issueVisible);
          } else {
            const selectedPanel = panels.find((panel) => panel.dataset.troubleshootingBrandPanel === selectedBrand);
            const selectedName = selectedPanel?.dataset.troubleshootingBrandName || window.gsT('troubleshooting.selected_brand_fallback');
            const control = modelControls.get(selectedBrand);
            const selectedModel = modelState.get(selectedBrand) || '';
            const visibleModelChoices = control
              ? control.buttons.filter((button) => !button.hidden && button.dataset.troubleshootingModel !== '__all__').length
              : 0;

            if (control && visibleModelChoices > 1 && !selectedModel) {
              status.textContent = window.gsT('troubleshooting.filter_choose_model', { brand: selectedName });
            } else if (selectedModel && selectedModel !== '__all__') {
              status.textContent = window.gsT('troubleshooting.showing_for_model', { brand: selectedName, model: selectedModel });
            } else {
              status.textContent = window.gsT('troubleshooting.showing_for', { brand: selectedName });
            }
          }
        } else if (selectedBrand) {
          const selectedPanel = panels.find((panel) => panel.dataset.troubleshootingBrandPanel === selectedBrand);
          const selectedName = selectedPanel?.dataset.troubleshootingBrandName || window.gsT('troubleshooting.selected_brand_fallback');
          const control = modelControls.get(selectedBrand);
          const selectedModel = modelState.get(selectedBrand) || '';

          if (control && !selectedModel) {
            status.textContent = window.gsT('troubleshooting.choose_model_for', { brand: selectedName });
          } else if (selectedModel && selectedModel !== '__all__') {
            status.textContent = window.gsT('troubleshooting.showing_for_model', { brand: selectedName, model: selectedModel });
          } else {
            status.textContent = window.gsT('troubleshooting.showing_for', { brand: selectedName });
          }
        } else {
          status.textContent = window.gsT('troubleshooting.status_idle');
        }
      }
    };

    filterSummaryClear.addEventListener('click', () => {
      selectedFilter = null;
      selectedBrand = '';
      modelState.clear();
      input.value = '';
      run();
      scrollToTarget(discoveryGroups[0] || root);
    });

    const clearSearch = () => {
      cancelPendingScroll();
      input.value = '';
      run();
      input.focus();
    };

    input.addEventListener('input', () => {
      cancelPendingScroll();
      run();
    });
    if (clear) clear.addEventListener('click', clearSearch);
    if (emptyClear) emptyClear.addEventListener('click', clearSearch);

    run();

    root.dataset.troubleshootingReady = 'true';
    root.removeAttribute('aria-busy');

    if (queuedActivations.length) {
      const pending = queuedActivations.splice(0);
      pending.forEach((target) => target.classList.remove('is-pending-activation'));
      pending.forEach((target) => {
        if (!target.hidden) target.click();
      });
    }
  };

  document.querySelectorAll('[data-gs-troubleshooting]').forEach((root) => {
    initializeRoot(root);
  });
})();
